import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { useEventSocket } from "@/hooks/useSockets";
import { api } from "@/lib/api";
import { BidDock, bidErrorMessage } from "@/pages/LotPage";

/* Contenedor del BidDock para el PERFIL COMPLETO del caballo (solo móvil).
   Capa de presentación sobre el MISMO estado autoritativo del engine:
   mismo payload /lots/{id}/live, mismo canal WS event:{id} y misma acción
   de puja que la página del lote — cero lógica de puja nueva.
   Rebind: se monta con key={lotId}; al cambiar de caballo se recarga el
   estado realtime ACTUAL del nuevo lote (jamás retiene datos del anterior). */
export const HorseLotDock = ({ lotId, lotUrl }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [live, setLive] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadLive = useCallback(
    () => api.get(`/lots/${lotId}/live`).then(({ data }) => setLive(data)),
    [lotId]
  );

  useEffect(() => {
    setLive(null); // rebind limpio: nunca mostrar el lote anterior
    loadLive().catch(() => setLive(null));
  }, [loadLive, user]);

  // Mismos tipos de mensaje que la página del lote (extensiones anti-sniping,
  // pujas, cierres y cambios de estado de la subasta llegan al instante).
  const onSocketMessage = useCallback(
    (msg) => {
      if (msg.type === "lot_update" && msg.lot?.id === lotId) {
        setLive((prev) => (prev ? { ...prev, lot: msg.lot } : prev));
        api.get(`/lots/${lotId}/live`).then(({ data }) => setLive(data)).catch(() => {});
      }
      if (msg.type === "lot_extended" && msg.lot_id === lotId) {
        setLive((prev) =>
          prev ? { ...prev, lot: { ...prev.lot, ends_at: msg.ends_at, extended_count: msg.extended_count } } : prev
        );
      }
      if (msg.type === "lot_closed" && msg.lot?.id === lotId) {
        api.get(`/lots/${lotId}/live`).then(({ data }) => setLive(data)).catch(() => {});
      }
      if (msg.type === "auction_status" || msg.type === "auction_ended" || msg.type === "auction_live") {
        setLive((prev) =>
          prev
            ? { ...prev, auction: { ...prev.auction, status: msg.status || (msg.type === "auction_ended" ? "ended" : "live") } }
            : prev
        );
      }
    },
    [lotId]
  );

  const { offsetRef } = useEventSocket(live?.lot?.event_id, onSocketMessage);

  // Misma acción y validación de puja que la página del lote (errores incluidos)
  const placeBid = async (body, onOk) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lots/${lotId}/bids`, body);
      setLive((prev) =>
        prev
          ? {
              ...prev,
              lot: data.lot,
              min_next_bid_cents: data.min_next_bid_cents,
              me: { ...prev.me, has_bid: true, leading: data.outcome === "leading", my_max_cents: body.max_cents || prev.me?.my_max_cents },
            }
          : prev
      );
      if (data.outcome === "leading") {
        toast.success(body.max_cents ? t("bid.maxSet") : t("bid.bidAccepted"));
      } else {
        toast(t("bid.bidDefendedToast"), { duration: 5000 });
      }
      onOk?.();
    } catch (err) {
      toast.error(bidErrorMessage(err, t, locale, live?.lot?.currency || "EUR"));
    } finally {
      setBusy(false);
    }
  };

  if (!live?.lot) return null;
  const lot = live.lot;
  // Mismos gates que el dock de la página del lote (solo timed abierto)
  const isLiveAuction = ["live", "hybrid"].includes(live.auction?.type);
  if (isLiveAuction || lot.status !== "open" || live.auction?.status === "ended") return null;

  return (
    <BidDock
      lot={lot}
      live={live}
      me={live.me}
      locale={locale}
      isAuthed={Boolean(user)}
      busy={busy}
      onBid={placeBid}
      onOpenRegister={() => navigate(lotUrl)}
      offsetRef={offsetRef}
      termsHref={`/auction-terms/${lot.event_id}`}
    />
  );
};
